import React from "react";

import { Box, BoxProps, Flex, Link } from "@catchoftheday/cg-components";
import { ThemeProvider } from "@catchoftheday/theme";
import { hideOnAndroidWebView } from "@catchoftheday/utilities";

import { useFooterDataQuery } from "../../generated/graphql";

import {
  AccordionNavigation,
  Body,
  ColumnNavigation,
  IconLinks,
  SubscribeInput,
  Title,
} from "./components";
import { AppDownloadLink, NavigationLinkColumns } from "./types";
import { px } from "./utils";

type FooterProps = {
  navigationLinkColumns: NavigationLinkColumns[];
  copyright: string;
  appDownloadLinks: AppDownloadLink[];
} & BoxProps;

function FooterInternal({
  copyright,
  navigationLinkColumns,
  appDownloadLinks,
  ...boxProps
}: FooterProps) {
  const [subscribed, setSubscribed] = React.useState(false);

  return (
    <ThemeProvider appName="footer">
      <Box
        as="footer"
        fontFamily=""
        paddingTop={["40px"]}
        paddingBottom={["40px"]}
        backgroundColor="#ffffff"
        borderTop="1px solid #ddd"
        fontWeight="100"
        fontSize="0.875rem"
        {...boxProps}
      >
        <Box maxWidth={[1340]} margin={["auto"]}>
          <Container
            display="flex"
            flexWrap="wrap"
            paddingLeft={{ xs: "0px", md: "20px" }}
            paddingRight={{ xs: "0px", md: "20px" }}
          >
            <Box
              display={{ xs: "none", md: "block" }}
              width={{ xs: "100%", lg: "70%" }}
              textAlign={{ xs: "center", lg: "left" }}
            >
              <ColumnNavigation navigationLinkColumns={navigationLinkColumns} />
            </Box>
            <Box
              display={{ xs: "block", md: "none" }}
              width={"100%"}
              marginBottom="30px"
            >
              <AccordionNavigation
                navigationLinkColumns={navigationLinkColumns}
              />
            </Box>

            <Box
              width={{ sm: "100%", md: "100%", lg: "30%" }}
              textAlign={{ xs: "center", lg: "left" }}
              paddingLeft={{ xs: "20px", md: "0px" }}
              paddingRight={{ xs: "20px", md: "0px" }}
            >
              <Title marginBottom="10px" marginTop="10px">
                Stay in Touch
              </Title>
              {subscribed ? (
                <Flex
                  data-testid="subscribe-success"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                >
                  <Body color="primary" fontWeight={700} marginTop={0}>
                    Cool! You are now subscribed to our newsletter.
                  </Body>
                  <Body color="primary" margin={0}>
                    Keep an eye on your inbox for our great deals!
                  </Body>
                </Flex>
              ) : (
                <React.Fragment>
                  <Body marginTop={0} marginBottom="17.5px">
                    Sign up for our newsletter to be the first to know about
                    daily deals, special events and new products!
                  </Body>
                  <SubscribeInput onSubmit={() => setSubscribed(true)} />
                  <Body fontSize={px(12)}>
                    View our <Link href="/privacy">Privacy Policy</Link> and{" "}
                    <Link href="/privacy#collection">Collection Statement</Link>
                  </Body>
                </React.Fragment>
              )}
            </Box>
          </Container>

          <IconLinks appDownloadLinks={appDownloadLinks} />

          <Container
            textAlign="center"
            dangerouslySetInnerHTML={{ __html: copyright }}
            fontSize={px(10)}
            fontWeight="lighter"
            color="typography.text"
            mt="0px"
            mb="12.5px"
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export const Footer = hideOnAndroidWebView(FooterInternal);

const Container = (props: BoxProps) => (
  <Box paddingRight="20px" paddingLeft="20px" {...props} />
);

export function WrappedFooter() {
  const { data } = useFooterDataQuery();

  if (!data) {
    return null;
  }

  const footer = data.footer;

  return (
    <Footer
      navigationLinkColumns={footer.navigationLinkColumns || []}
      copyright={footer.copyright}
      appDownloadLinks={footer.appDownloadLinks}
    />
  );
}
